/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Fragment, useRef, useState } from 'react'
import { connect } from 'react-redux'
import scoringActions from '@sportninja/common/actions/scoring'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import { ACTIONS } from '@sportninja/common/reducers/game-timeline-parser'
import { getOfflineId } from '@sportninja/common/reducers/utils'
import { t } from '@sportninja/common/i18n'
import { TIMEOUT_TEXT } from '@sportninja/common/features/game/scoring/constants/timeout'
import { planTimelineTimeoutCreate } from '@sportninja/common/features/game/scoring/utils/timelineTimeoutCreate'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { getBrowserTimezone } from '@sportninja/common/ts-utils/timezone-display'
import permanentDstPlugin from '@sportninja/common/ts-utils/dayjs-permanent-dst'
import { toISOString } from '@sportninja/common/utils/utils'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import Sheet from '../../../components/Sheet'
import Form from '../../../components/Form'
import { SelectFormInput } from '../../../components/Select'
import {
  scoringGoalForm,
  scoringGoalieChangeForm,
  scoringPenaltyForm,
  scoringTimeoutForm,
} from '../../../components/Form/form-configs'
import { FormButton, FormRow } from '../../../components/Form/css'
import { Mobile } from '../../../components/Responsive'
import Icon from '../../../components/Icon'
import { font } from '../../../components/css'
import { getPlayingRoster } from '.'

// `inViewerZone` below calls `.tz()` and formats the `z` token. Registering the
// plugins here (the house pattern) rather than relying on the app entry having
// run first, so this module works standalone. `extend` is idempotent.
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)
// Keeps a zone that abolished its clock change reading on the offset it
// actually keeps, on a runtime whose database still has the old rule.
dayjs.extend(permanentDstPlugin)

/**
 * An instant read on the viewer's own clock, with its zone named.
 *
 * The zone has to be named even though it is the viewer's own: a bare
 * `dayjs(iso)` is resolved by the engine's timezone database, which the
 * permanent-DST plugin never sees, so a viewer in a province that abolished
 * its clock change reads an hour early and gets a 'PST' label. `.tz()` is the
 * seam the plugin patches. See ts-utils/dayjs-permanent-dst.
 *
 * An unset or unparseable instant is handed back untouched, so it still
 * formats as 'Invalid Date' rather than throwing inside the timezone plugin.
 */
const inViewerZone = (iso) => {
  const value = dayjs(iso)
  const zone = getBrowserTimezone()
  return zone && value.isValid() ? value.tz(zone) : value
}

const findPlayerById = (players, id) => {
  if (!players) {
    return null
  }
  if (!id) {
    return null
  }
  return players[id]
}

const EventForm = ({
  createGoal,
  createGoalChange,
  createPenalty,
  createTimeout,
  gameId,
  onCancel,
  periods,
  onComplete,
  roster,
  teamId,
  toggleOpen,
  type,
  started_at,
  isSoccer = false,
  homeGameRoster = [],
  visitingGameRoster = [],
  sportId = null,
  isGenericGame = false,
  scoringValues = [],
}) => {
  // The last timeout create sent from this form, until one is answered:
  // another try at the same timeout goes under the same id.
  const timeoutAttempt = useRef(null)
  let form
  let onSubmit

  switch (type) {
    case ACTIONS.goal: {
      form = scoringGoalForm(
        { periods, roster, started_at },
        { includePeriod: true },
        isSoccer,
        sportId,
        false,
        isGenericGame,
        scoringValues
      )
      onSubmit = async (form) => {
        const { period, ...body } = form
        if (body.player_id === 'unassigned') {
          body.player_id = null
          body.is_bench_edit = true
        }
        await createGoal(gameId, period, { ...body, team_id: teamId })
        await onComplete()
        toggleOpen({ forceClose: true })
      }
      break
    }
    case ACTIONS.goalieChange: {
      form = scoringGoalieChangeForm(
        { periods, roster },
        { includePeriod: true },
        isSoccer,
        sportId
      )
      onSubmit = async (form) => {
        const { period, ...body } = form
        const player = findPlayerById(roster, body.goalie_id)
        body.player_id = body.goalie_id || null
        body.previous_player_id = body.previous_goalie_id || null
        body.player_type_id = player?.playerTypeId || null
        await createGoalChange(gameId, period, { ...body, team_id: teamId })
        await onComplete()
        toggleOpen({ forceClose: true })
      }
      break
    }
    case ACTIONS.penalty: {
      form = scoringPenaltyForm(
        { periods, roster },
        { includePeriod: true },
        isSoccer,
        sportId
      )
      onSubmit = async (form) => {
        const { period, ...body } = form
        await createPenalty(gameId, period, { ...body, team_id: teamId })
        await onComplete()
        toggleOpen({ forceClose: true })
      }
      break
    }
    case ACTIONS.timeout: {
      form = scoringTimeoutForm({ periods }, { includePeriod: true }, isSoccer)
      onSubmit = async ({ period, period_clock_time }) => {
        const plan = planTimelineTimeoutCreate(
          {
            periodId: period,
            teamId,
            clock: period_clock_time,
            occurredAt: toISOString(dayjs()),
          },
          timeoutAttempt.current,
          getOfflineId
        )
        timeoutAttempt.current = plan.attempt
        await createTimeout(gameId, plan.periodId, plan.form)
        timeoutAttempt.current = null
        await onComplete()
        toggleOpen({ forceClose: true })
      }
      break
    }
    default:
      return false
  }

  return <Form key={type} form={form} onCancel={onCancel} onSubmit={onSubmit} />
}

const mapDispatchToProps = (dispatch) => {
  return {
    createGoal: bindActionToPromise(
      dispatch,
      scoringActions.goals.create.request
    ),
    createGoalChange: bindActionToPromise(
      dispatch,
      scoringActions.goalieChanges.create.request
    ),
    createPenalty: bindActionToPromise(
      dispatch,
      scoringActions.penalties.create.request
    ),
    createTimeout: bindActionToPromise(
      dispatch,
      scoringActions.timeouts.create.request
    ),
  }
}

const ConnectedEventForm = connect(null, mapDispatchToProps)(EventForm)

const AddNewTimelineEvent = ({
  league_game_number,
  started_at,
  ended_at,
  homeTeam,
  homeGameRoster,
  gameId,
  periods,
  visitingTeam,
  visitingGameRoster,
  disabled,
  onComplete,
  isSoccer = false,
  sportId = null,
  isGenericGame = false,
  scoringValues = [],
  trackTimeouts = false,
}) => {
  let TYPES = {
    [ACTIONS.goal]: {
      label: t('common:goal'),
      title: t('Web:addNewGoal'),
    },
    [ACTIONS.goalieChange]: {
      label: isSoccer ? 'Substitution' : t('common:goalieChange'),
      title: isSoccer ? 'Add new Substitution' : t('Web:addNewGoalieChange'),
    },
    [ACTIONS.penalty]: {
      label: isSoccer ? 'Foul' : t('common:penalty'),
      title: isSoccer ? 'Add new Foul' : t('Web:addNewPenalty'),
    },
  }

  // Offered only where the competition tracks timeouts. Timeouts already
  // recorded stay editable on the timeline either way.
  if (trackTimeouts) {
    TYPES[ACTIONS.timeout] = {
      label: t(TIMEOUT_TEXT.event),
      title: capitalizeWords(t(TIMEOUT_TEXT.newTitle)),
    }
  }

  if (isGenericGame) {
    TYPES = {
      [ACTIONS.goal]: {
        label: 'Point',
        title: 'Add New Point',
      },
    }
  }

  const [type, setType] = useState('')
  const [teamId, setTeamId] = useState('')

  const title = type !== '' ? TYPES[type].title : t('Web:addNewEvent')
  const onTypeChange = ({ target }) => setType(target.value)
  const onTeamChange = ({ target }) => setTeamId(target.value)

  let roster = {}
  if (homeTeam && visitingTeam) {
    roster = getPlayingRoster(
      teamId === homeTeam.id ? homeGameRoster : visitingGameRoster
    )
  }

  const handleCancel = (onClick) => {
    onClick()
    setType('')
    setTeamId('')
  }

  return (
    <Sheet
      button={(toggleOpen) => (
        <div
          css={css`
            width: 170px;
            min-width: 125px;
            margin-left: 8px;
          `}
        >
          <FormButton
            disabled={disabled}
            onClick={handleCancel.bind(this, toggleOpen)}
            css={css`
              height: 40px;
            `}
          >
            Add Event
            <Icon
              name='plus'
              css={css`
                margin-left: 12px;
              `}
            />
          </FormButton>
        </div>
      )}
      title={title}
    >
      {(toggleOpen) => {
        return (
          <Fragment>
            <div
              css={css`
                margin-top: -16px;
                margin-bottom: 30px;
                font-size: 13px;
                line-height: 14px;
              `}
            >
              <div
                css={css`
                  font-size: 15px;
                  ${font.title}
                  font-weight: 700;
                  margin-bottom: 4px;
                `}
              >
                Game #{league_game_number}
              </div>
              <div>Home: {homeTeam?.name_full}</div>
              <div>Visitor: {visitingTeam?.name_full}</div>
              <div>
                Start: {inViewerZone(started_at).format('MMM D, h:mm a z')}
              </div>
              <div>End: {inViewerZone(ended_at).format('MMM D, h:mm a z')}</div>
            </div>
            <Mobile>
              {(isMobile) => (
                <FormRow
                  css={css`
                    margin-bottom: 20px;
                  `}
                  noFlex
                  column={isMobile}
                >
                  <SelectFormInput
                    name='type'
                    label={t('Web:typeOfEvent')}
                    onChange={onTypeChange}
                    required
                  >
                    <option value='' disabled>
                      {t('Web:selectTypeOfEvent')}:
                    </option>
                    <option disabled>-----</option>
                    {Object.keys(TYPES).map((key) => {
                      return (
                        <option key={key} value={key}>
                          {TYPES[key].label}
                        </option>
                      )
                    })}
                  </SelectFormInput>
                  <SelectFormInput
                    name='team'
                    label={t('util:capitalize', { text: t('common:team') })}
                    onChange={onTeamChange}
                    required
                  >
                    <option value='' disabled>
                      {t('Web:selectATeam')}:
                    </option>
                    <option disabled>-----</option>
                    {homeTeam && (
                      <option value={homeTeam.id}>{homeTeam.name_full}</option>
                    )}
                    {visitingTeam && (
                      <option value={visitingTeam.id}>
                        {visitingTeam.name_full}
                      </option>
                    )}
                  </SelectFormInput>
                </FormRow>
              )}
            </Mobile>

            {type !== '' && teamId !== '' && (
              <ConnectedEventForm
                gameId={gameId}
                onCancel={handleCancel.bind(this, toggleOpen)}
                periods={periods}
                roster={roster}
                teamId={teamId}
                toggleOpen={toggleOpen}
                type={type}
                onComplete={onComplete}
                started_at={started_at}
                isSoccer={isSoccer}
                homeGameRoster={homeGameRoster}
                visitingGameRoster={visitingGameRoster}
                sportId={sportId}
                isGenericGame={isGenericGame}
                scoringValues={scoringValues}
              />
            )}
          </Fragment>
        )
      }}
    </Sheet>
  )
}

export default AddNewTimelineEvent
